import React, {
  ComponentPropsWithoutRef,
  ElementType,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Icon from '../../../../common/components/icon/Icon';
import Responsive from '../../../../common/components/responsive/Responsive';

import './SearchField.scss';

export interface SearchFieldProps
  extends ComponentPropsWithoutRef<ElementType> {
  className;
  value;
  onChange;
  onSearch;
  onClear;
  onBack;
}

const SearchField = ({
  className,
  value,
  onChange,
  onSearch,
  onClear,
  onBack,
  ...props
}: SearchFieldProps) => {
  const handleValueChange = useCallback(
    ({ target: { value } }) => {
      onChange(value);
    },
    [onChange],
  );

  const handleClearClick = useCallback(() => {
    if (onClear) {
      onClear();
    } else {
      onChange('');
    }
  }, [onChange, onClear]);

  const handleKeyPress = useCallback(
    ({ key }) => {
      if (onSearch && key === 'Enter') {
        onSearch();
      }
    },
    [onSearch],
  );

  return (
    <div
      className={classnames(
        'search-field',
        { 'search-field_has-value': value },
        className,
      )}
    >
      <Responsive mobile={false}>
        {onBack && (
          <button
            className="search-field__back-button"
            title="Back"
            onClick={onBack}
          >
            <Icon name="back" />
          </button>
        )}
      </Responsive>
      <Responsive mobile={true}>
        <button
          className="search-field__search-button"
          title="Find places"
          onClick={onSearch}
        >
          <Icon
            className="search-field__search-button-icon"
            name="magnifying-glass"
          />
        </button>
      </Responsive>
      <input
        {...props}
        className="search-field__input"
        placeholder="Search"
        value={value}
        onChange={handleValueChange}
        onKeyPress={handleKeyPress}
      />
      <Responsive mobile={false}>
        <button
          className="search-field__search-button"
          title="Find places"
          onClick={onSearch}
        >
          <Icon
            className="search-field__search-button-icon"
            name="magnifying-glass"
          />
        </button>
        <div className="search-field__buttons-separator" />
      </Responsive>
      <button
        className="search-field__clear-button"
        title="Clear search"
        onClick={handleClearClick}
      >
        <Icon className="search-field__clear-button-icon" name="cross" />
      </button>
    </div>
  );
};

SearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onSearch: PropTypes.func,
  onBack: PropTypes.func,
};

export default SearchField;
